import { useEffect, useMemo, useState } from 'react'

import { Variants } from 'motion/react'
import { useMediaQuery } from 'shared/hooks/useMediaQuery'
import { SnapArea, WindowState } from 'shared/types'

import { useWindowStore } from '../../../../stores'

export type SnapRect = {
  x: number
  y: number
  width: number | string
  height: number | string
}

export enum WindowVariant {
  VISIBLE = 'visible',
  SNAPPED = 'snapped',
  MAXIMIZED = 'maximized',
  MINIMIZED = 'minimized'
}

interface UseWindowVariantsParams {
  window: WindowState
  initialPosition: { x: number; y: number }
  constraintsRef: React.RefObject<HTMLDivElement>
  getSnapRect: (area: SnapArea) => SnapRect
  topDockHeight: number
}

/**
 * Returns animation variants that only animate position, opacity, scale, and display.
 * Width/height are handled separately via CSS + layout prop for better performance.
 */
export function useWindowVariants({
  window,
  initialPosition,
  constraintsRef,
  getSnapRect,
  topDockHeight
}: UseWindowVariantsParams): Variants {
  const [offsetTop, setOffsetTop] = useState<number | null>(null)

  useEffect(() => {
    setOffsetTop(constraintsRef.current?.offsetTop || 0)
  }, [constraintsRef, topDockHeight])

  return {
    initial: {
      originX: 0.5,
      originY: 0,
      opacity: 0,
      scale: 0.1,
      y: initialPosition.y,
      x: initialPosition.x - window.size.width / 2,
      display: 'none'
    },
    visible: {
      opacity: 1,
      scale: 1,
      y: window.position.y,
      x: window.position.x,
      display: 'flex'
    },
    snapped: (() => {
      if (!window.snapArea) return {}
      const rect = getSnapRect(window.snapArea)
      return {
        opacity: 1,
        scale: 1,
        y: rect.y,
        x: rect.x,
        display: 'flex'
      }
    })(),
    maximized: {
      opacity: 1,
      scale: 1,
      y: offsetTop ?? 0,
      x: 0,
      display: 'flex'
    },
    minimized: {
      opacity: 0,
      scale: 0.1,
      y: initialPosition.y,
      x: initialPosition.x - window.size.width / 2,
      display: 'none'
    },
    closed: {
      opacity: 0,
      scale: 1.05,
      display: 'none'
    }
  }
}

export interface UseWindowVariantResult {
  variant: WindowVariant | undefined
}

/**
 * Returns the current window variant (visible, snapped, maximized, minimized) for a window
 * based on viewport, window state, and active window.
 */
export function useGetCurrentWindowVariant(
  window: WindowState | undefined
): UseWindowVariantResult {
  const { isMobile, isTablet, isDesktop } = useMediaQuery()
  const activeWindowId = useWindowStore(state => state.activeWindowId)

  const variant = useMemo((): WindowVariant | undefined => {
    if (!window) return undefined

    if (isDesktop) {
      if (window.isMinimized) return WindowVariant.MINIMIZED
      if (window.isMaximized) return WindowVariant.MAXIMIZED
      if (window.snapArea) return WindowVariant.SNAPPED
      return WindowVariant.VISIBLE
    }
    if (isTablet || isMobile) {
      return activeWindowId === window.id
        ? WindowVariant.MAXIMIZED
        : WindowVariant.MINIMIZED
    }
    return undefined
  }, [window, isDesktop, isTablet, isMobile, activeWindowId])

  return { variant }
}

/**
 * Returns the CSS dimensions for the window based on its current variant.
 * Used with Framer Motion's layout prop for GPU-accelerated size animations.
 */
export function useWindowDimensions({
  window,
  windowVariant,
  getSnapRect
}: {
  window: WindowState
  windowVariant: WindowVariant | undefined
  getSnapRect: (area: SnapArea) => SnapRect
}): { width: number | string; height: number | string } {
  return useMemo(() => {
    switch (windowVariant) {
      case WindowVariant.MAXIMIZED:
        return { width: '100%', height: '100%' }
      case WindowVariant.SNAPPED: {
        if (!window.snapArea) {
          return { width: window.size.width, height: window.size.height }
        }
        const rect = getSnapRect(window.snapArea)
        return { width: rect.width, height: rect.height }
      }
      case WindowVariant.VISIBLE:
      case WindowVariant.MINIMIZED:
      default:
        return { width: window.size.width, height: window.size.height }
    }
  }, [
    windowVariant,
    window.snapArea,
    window.size.width,
    window.size.height,
    getSnapRect
  ])
}
